import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightCircle,
  ArrowUp,
  Bell,
  Check,
  CheckCircle2,
  CheckSquare,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Clock,
  CloudOff,
  Delete,
  Eye,
  EyeOff,
  Gift,
  GitBranch,
  Info,
  Lightbulb,
  Lock,
  LocateFixed,
  LogOut,
  MapPin,
  MessageCircle,
  Mic,
  Navigation,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Smartphone,
  Store,
  TrendingUp,
  X,
  type LucideIcon,
} from "lucide-react";

/**
 * Table de correspondance Ionicons → Lucide.
 *
 * `@expo/vector-icons` était la 2ᵉ dépendance la plus diffuse du projet natif
 * (47 fichiers, 90 usages, 35 icônes distinctes) et n'apparaissait même pas
 * dans package.json — elle arrivait en transitif via `expo`.
 *
 * Cette table existe pour que le portage des écrans (Lot 6) soit MÉCANIQUE :
 *   -<Ionicons name="chevron-back" size={20} color={colors.navy800} />
 *   +<Icon name="chevron-back" size={20} color={colors.navy800} />
 * Sans elle, chaque écran rejouerait l'arbitrage « quelle icône Lucide pour
 * quelle Ionicon », avec des choix divergents d'un écran à l'autre.
 *
 * ## Deux différences de fond avec Ionicons
 *
 * 1. **Pas de variantes `-outline`.** Ionicons livrait chaque glyphe en plein
 *    et en contour ; Lucide n'a QUE du contour. Les paires (`location` /
 *    `location-outline`) pointent donc vers la même icône. Là où le plein
 *    portait une information — l'onglet actif de la tab bar, par exemple —
 *    c'est désormais la COULEUR et la pastille de fond qui distinguent l'état,
 *    ce que fait déjà `AppShell`. À vérifier à l'œil sur les écrans qui
 *    juxtaposaient les deux variantes.
 *
 * 2. **Le trait est plus fin.** Lucide dessine en 2px sur une grille de 24 ;
 *    Ionicons outline était plus épais. `strokeWidth` est exposé pour
 *    rattraper ponctuellement, mais mieux vaut ne pas le faire au cas par cas :
 *    l'homogénéité prime.
 */
const ICONS = {
  add: Plus,
  // `alert` / `warning-outline` / `information` ne viennent pas des balises
  // <Ionicons> mais des tables de tons d'AppDialog, où le nom d'icône est une
  // donnée. Les oublier ferait échouer le rendu d'un dialogue selon son ton.
  alert: AlertTriangle,
  "warning-outline": AlertTriangle,
  information: Info,
  "git-branch": GitBranch,
  "phone-portrait": Smartphone,
  "swap-horizontal": ArrowLeftRight,
  "time-outline": Clock,
  "eye-outline": Eye,
  "eye-off-outline": EyeOff,
  "bulb-outline": Lightbulb,
  "alert-circle": AlertCircle,
  "alert-circle-outline": AlertCircle,
  "arrow-down": ArrowDown,
  "arrow-forward": ArrowRight,
  "arrow-forward-circle-outline": ArrowRightCircle,
  "arrow-up": ArrowUp,
  "backspace-outline": Delete,
  "checkbox-outline": CheckSquare,
  checkmark: Check,
  "checkmark-circle": CheckCircle2,
  "chevron-back": ChevronLeft,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "chevron-forward": ChevronRight,
  close: X,
  // Agrandir / réduire une carte. `Maximize2`/`Minimize2` sont les deux flèches
  // opposées — le pictogramme que tout le monde reconnaît sur une vidéo ou une
  // carte, là où `Expand` de lucide dessine quatre flèches, plus confus à 16 px.
  expand: Maximize2,
  contract: Minimize2,
  "cloud-offline": CloudOff,
  "create-outline": Pencil,
  "cube-outline": Package,
  gift: Gift,
  "information-circle": Info,
  "information-circle-outline": Info,
  locate: LocateFixed,
  location: MapPin,
  "location-outline": MapPin,
  "location-sharp": MapPin,
  "lock-closed": Lock,
  "log-out-outline": LogOut,
  "chatbubble-ellipses-outline": MessageCircle,
  mic: Mic,
  navigate: Navigation,
  "notifications-outline": Bell,
  refresh: RefreshCw,
  search: Search,
  "shield-checkmark": ShieldCheck,
  "shield-checkmark-outline": ShieldCheck,
  "storefront-outline": Store,
  "trending-up": TrendingUp,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

type IconProps = {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
  /**
   * Description pour les lecteurs d'écran. Par défaut l'icône est DÉCORATIVE
   * et masquée : dans la quasi-totalité des cas elle double un libellé voisin,
   * et l'annoncer créerait une répétition. Ne renseigner que si l'icône porte
   * seule l'information (bouton sans texte).
   */
  label?: string;
};

export function Icon({
  name,
  size = 20,
  color = "currentColor",
  strokeWidth = 2,
  className,
  label,
}: IconProps) {
  const Glyph = ICONS[name];

  return (
    <Glyph
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    />
  );
}
